import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { X, ZoomIn } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicGallery = () => {
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setImages(response.data);
    } catch (error) {
      console.error('Failed to fetch gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" data-testid="public-gallery-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-heading font-bold tracking-tight leading-none uppercase mb-4">
            OUR <span className="text-primary">GALLERY</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Explore our collection of products, installations, and success stories
          </p>
        </div>

        {images.length === 0 ? (
          <Card className="p-12 text-center border-slate-200">
            <div className="max-w-md mx-auto">
              <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ZoomIn className="h-12 w-12 text-slate-400" />
              </div>
              <h2 className="text-2xl font-heading font-bold mb-2">Gallery Coming Soon</h2>
              <p className="text-slate-600">
                We're building our gallery. Check back soon to see our amazing products and installations!
              </p>
            </div>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {images.map((image, index) => (
                <Card
                  key={image.id}
                  className="group overflow-hidden border-slate-200 hover:shadow-2xl transition-all duration-300 cursor-pointer"
                  onClick={() => setSelectedImage(image)}
                  data-testid={`gallery-image-${index}`}
                >
                  <div className="aspect-square relative overflow-hidden bg-slate-100">
                    <img
                      src={image.image_url}
                      alt={image.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/400?text=Image+Not+Available';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <h3 className="text-white font-semibold text-lg truncate">
                          {image.title}
                        </h3>
                        {image.description && (
                          <p className="text-white/80 text-sm line-clamp-2 mt-1">
                            {image.description}
                          </p>
                        )}
                      </div>
                      <div className="absolute top-4 right-4 bg-white/90 rounded-full p-2">
                        <ZoomIn className="h-5 w-5 text-slate-900" />
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            <div className="mt-12 text-center">
              <p className="text-slate-500 text-sm">
                Showing {images.length} {images.length === 1 ? 'image' : 'images'}
              </p>
            </div>
          </>
        )}
      </div>

      {/* Lightbox Modal */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-5xl w-full p-0 bg-transparent border-0 shadow-none">
          {selectedImage && (
            <div className="relative">
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute -top-12 right-0 bg-white/90 hover:bg-white rounded-full p-2 transition-colors z-50"
                data-testid="close-lightbox"
              >
                <X className="h-6 w-6 text-slate-900" />
              </button>
              <div className="bg-white rounded-lg overflow-hidden">
                <img
                  src={selectedImage.image_url}
                  alt={selectedImage.title}
                  className="w-full max-h-[80vh] object-contain"
                />
                <div className="p-6 bg-slate-50">
                  <h2 className="text-2xl font-heading font-bold mb-2">
                    {selectedImage.title}
                  </h2>
                  {selectedImage.description && (
                    <p className="text-slate-700 mb-4">{selectedImage.description}</p>
                  )}
                  <div className="flex items-center justify-between text-sm text-slate-500">
                    <span>Added: {new Date(selectedImage.created_at).toLocaleDateString()}</span>
                    <a
                      href={selectedImage.image_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline font-semibold"
                    >
                      View Original
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicGallery;
